/**
 * LINE ニュース 1 セクションのテキスト編集カード（image 以外）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10.5
 *
 * メールの SectionCard と異なり AI 再生成 / 壁打ち機能はなし（v1）。
 * 画像セクションは ImageSectionEditor が別途担当。
 */
import { Check, Edit3, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';

import type { LineNewsSection } from '@goenhub/shared';

interface Props {
  section: LineNewsSection;
  onChange: (next: LineNewsSection) => void;
  onDelete: () => void;
}

export function NewsSectionCard({ section, onChange, onDelete }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.content);

  const startEdit = () => {
    setDraft(section.content);
    setEditing(true);
  };
  const commit = () => {
    onChange({ ...section, content: draft });
    setEditing(false);
  };
  const cancel = () => {
    setDraft(section.content);
    setEditing(false);
  };

  return (
    <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)]">
      <div className="flex items-center justify-between p-2 bg-[var(--bg-hover)] border-b border-[var(--border)] rounded-t text-xs">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{section.label}</span>
          <code className="text-[10px] font-mono text-[var(--text-muted)]">
            {section.type}
          </code>
        </div>
        <div className="flex gap-0.5">
          {!editing ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={startEdit}
                title="編集"
              >
                <Edit3 className="size-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onDelete}
                title="削除"
              >
                <Trash2 className="size-3" />
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" size="icon" onClick={commit} title="確定">
                <Check className="size-3" />
              </Button>
              <Button type="button" variant="ghost" size="icon" onClick={cancel} title="取消">
                <X className="size-3" />
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="p-3 text-[13px] whitespace-pre-wrap leading-relaxed">
        {editing ? (
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.max(3, draft.split('\n').length + 1)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancel();
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') commit();
            }}
          />
        ) : (
          section.content || (
            <span className="text-[var(--text-muted)] italic">（未入力）</span>
          )
        )}
      </div>
      {section.type === 'cta' && (
        <div className="p-3 border-t border-[var(--border)] space-y-2">
          <Input
            type="text"
            placeholder="ボタンラベル（例: 詳しくはこちら）"
            value={section.cta_button_label ?? ''}
            onChange={(e) =>
              onChange({ ...section, cta_button_label: e.target.value })
            }
          />
          <Input
            type="url"
            placeholder="https://... （CTA リンク URL）"
            value={section.cta_url_placeholder ?? ''}
            onChange={(e) =>
              onChange({ ...section, cta_url_placeholder: e.target.value })
            }
          />
        </div>
      )}
    </div>
  );
}
